import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  ADVERT_APPROVALS,
  ADVERT_DAY_CHOICES,
  ADVERT_STATUS_LABELS,
  BOARD_SIZES,
  BOARD_SIZE_KEYS,
  boardName,
  formatPence,
  type AdminAdvert,
  type AdvertApproval,
  type AdvertDecision,
  type AdvertSettings,
} from '@london-life/contracts';
import { Badge } from '@london-life/ui/components/badge';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { Textarea } from '@london-life/ui/components/textarea';
import { FormDialog } from '@london-life/ui/admin/form-dialog';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { useAdmin } from '../lib/admin-context';
import { when } from '../lib/format';
import { useRequest } from '../lib/use-request';

type Show = 'waiting' | 'live' | 'all';
const SHOW_LABEL: Record<Show, string> = { waiting: 'Waiting', live: 'Live now', all: 'All ads' };

// an ad's picture (fetched with the admin's session: the page only shows pictures of its own)
function Picture({ advert }: { advert: AdminAdvert }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    let made: string | null = null;
    void api.admin
      .advertPicture(advert.image)
      .then((file) => {
        if (!live) return;
        made = URL.createObjectURL(file);
        setUrl(made);
      })
      .catch(() => {});
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [advert.image]);
  return <span className="admin-advert-picture">{url ? <img src={url} alt={`${advert.title} advert`} /> : null}</span>;
}

// What players put on the map's boards (as in Lagos Life). The team checks the ones waiting (approve, or reject
// with a reason: the player is refunded), takes down live ones that break the rules (no refund), and sets the
// prices, the runs on offer and whether ads are checked first.
export function AdvertsPage() {
  const { can } = useAdmin();
  const [show, setShow] = useState<Show>('waiting');
  const list = useRequest(useCallback(() => api.admin.adverts(show), [show]));
  const [deciding, setDeciding] = useState<{ advert: AdminAdvert; action: AdvertDecision['action'] } | null>(null);
  const [reason, setReason] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const decide = async (advert: AdminAdvert, action: AdvertDecision['action']) => {
    setDone(null);
    await api.admin.decideAdvert(advert.id, action === 'approve' ? { action } : { action, reason: reason.trim() });
    setDone(
      action === 'approve'
        ? `"${advert.title}" is live.`
        : action === 'reject'
          ? `"${advert.title}" was rejected and refunded.`
          : `"${advert.title}" was taken down.`,
    );
    await list.reload();
  };
  const adverts = list.data?.adverts ?? [];
  return (
    <section>
      <PageHeader
        title="Adverts"
        description="Ads players put on the map's boards. Check the ones waiting, take down any that break the rules, and set the prices."
      />
      <div className="admin-toolbar">
        {(Object.keys(SHOW_LABEL) as Show[]).map((key) => (
          <Button key={key} type="button" variant={show === key ? 'default' : 'outline'} onClick={() => setShow(key)}>
            {SHOW_LABEL[key]}
            {key === 'waiting' && list.data ? ` (${list.data.waiting})` : ''}
          </Button>
        ))}
      </div>
      {list.error && <ErrorNotice error={list.error} onRetry={() => void list.reload()} />}
      {done && (
        <p role="status" className="admin-muted">
          {done}
        </p>
      )}
      {list.busy && !list.data && <p className="admin-muted">Loading…</p>}
      {list.data && adverts.length === 0 && (
        <div className="admin-empty">
          <strong className="admin-empty-title">{show === 'waiting' ? 'Nothing to check.' : 'No ads here.'}</strong>
        </div>
      )}
      <ul className="admin-reports">
        {adverts.map((advert) => (
          <li key={advert.id} className="admin-panel admin-advert" data-testid="advert">
            <Picture advert={advert} />
            <div className="admin-advert-body">
              <div className="admin-player-head">
                <h2>{advert.title}</h2>
                <Badge variant={advert.status === 'pending' ? 'default' : 'secondary'}>
                  {ADVERT_STATUS_LABELS[advert.status]}
                </Badge>
              </div>
              <dl className="admin-facts">
                <dt>By</dt>
                <dd>{advert.owner.username ? `@${advert.owner.username}` : advert.owner.firstName}</dd>
                <dt>Board</dt>
                <dd>
                  {boardName(advert.board, advert)} · {advert.days} {advert.days === 1 ? 'day' : 'days'} ·{' '}
                  {formatPence(advert.pricePence)}
                </dd>
                <dt>Link</dt>
                <dd className="admin-advert-link">{advert.link ?? 'None'}</dd>
                <dt>Booked</dt>
                <dd>{when(advert.createdAt)}</dd>
                {advert.startsAt && advert.endsAt && (
                  <>
                    <dt>Runs</dt>
                    <dd>
                      {when(advert.startsAt)} to {when(advert.endsAt)}
                    </dd>
                  </>
                )}
                {advert.reason && (
                  <>
                    <dt>Reason</dt>
                    <dd>{advert.reason}</dd>
                  </>
                )}
              </dl>
              {can('ads.write') && (advert.status === 'pending' || advert.status === 'live') && (
                <div className="admin-page-actions wrap">
                  {advert.status === 'pending' ? (
                    <>
                      <Button type="button" onClick={() => setDeciding({ advert, action: 'approve' })}>
                        Approve
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setReason('');
                          setDeciding({ advert, action: 'reject' });
                        }}
                      >
                        Reject and refund
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => {
                        setReason('');
                        setDeciding({ advert, action: 'remove' });
                      }}
                    >
                      Take down
                    </Button>
                  )}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
      <FormDialog
        open={!!deciding}
        title={
          deciding?.action === 'approve'
            ? `Put "${deciding.advert.title}" on the board`
            : deciding?.action === 'reject'
              ? 'Reject this ad'
              : 'Take this ad down'
        }
        description={
          deciding?.action === 'approve'
            ? `It goes live now for ${deciding.advert.days} ${deciding.advert.days === 1 ? 'day' : 'days'}.`
            : deciding?.action === 'reject'
              ? `The player gets their ${formatPence(deciding.advert.pricePence)} back and is told why.`
              : 'For ads that break the rules: it comes off the boards at once and is not refunded. The player is told why.'
        }
        submitLabel={
          deciding?.action === 'approve' ? 'Approve' : deciding?.action === 'reject' ? 'Reject' : 'Take down'
        }
        canSubmit={deciding?.action === 'approve' || reason.trim().length >= 3}
        onSubmit={() => (deciding ? decide(deciding.advert, deciding.action) : Promise.resolve())}
        onClose={() => setDeciding(null)}
      >
        {deciding?.action !== 'approve' && (
          <div className="admin-field">
            <Label htmlFor="advert-reason">Reason (the player sees it)</Label>
            <Textarea
              id="advert-reason"
              maxLength={300}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        )}
      </FormDialog>
      <AdvertSettingsPanel editable={can('ads.write')} />
    </section>
  );
}

// prices, runs on offer, checking, turns and how many ads a board takes
function AdvertSettingsPanel({ editable }: { editable: boolean }) {
  const current = useRequest(useCallback(() => api.admin.advertSettings(), []));
  const [draft, setDraft] = useState<AdvertSettings | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (current.data) setDraft(current.data.settings);
  }, [current.data]);
  if (!draft) return current.error ? <ErrorNotice error={current.error} onRetry={() => void current.reload()} /> : null;
  const set = (next: Partial<AdvertSettings>) => {
    setSaved(false);
    setDraft({ ...draft, ...next });
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await api.admin.saveAdvertSettings(draft);
      setSaved(true);
      await current.reload();
    } catch (problem) {
      current.fail(problem);
    }
  };
  return (
    <form className="admin-panel admin-form" onSubmit={(event) => void submit(event)} aria-label="Advert settings">
      <h2>Settings</h2>
      <fieldset disabled={!editable} className="admin-advert-settings">
        <div className="admin-field">
          <Label htmlFor="advert-approval">Checking</Label>
          <select
            id="advert-approval"
            className="content-select"
            value={draft.approval}
            onChange={(event) => set({ approval: event.target.value as AdvertApproval })}
          >
            {(Object.keys(ADVERT_APPROVALS) as AdvertApproval[]).map((key) => (
              <option key={key} value={key}>
                {ADVERT_APPROVALS[key]}
              </option>
            ))}
          </select>
        </div>
        <div className="admin-field">
          <span className="admin-label">Price for 7 days (£)</span>
          <div className="admin-advert-prices">
            {[...BOARD_SIZE_KEYS, 'plot' as const].map((size) => (
              <label key={size}>
                <span>{size === 'plot' ? 'River plot (1×1)' : BOARD_SIZES[size].label}</span>
                <Input
                  type="number"
                  min={size === 'plot' ? 0.1 : 1}
                  step={0.01}
                  value={draft.weekPence[size] / 100}
                  onChange={(event) =>
                    set({
                      weekPence: { ...draft.weekPence, [size]: Math.round(Number(event.target.value) * 100) },
                    })
                  }
                />
              </label>
            ))}
          </div>
          <p className="admin-muted">Other runs cost the same per day. A 2×2 river plot is four plots, a 3×3 nine.</p>
        </div>
        <div className="admin-field">
          <span className="admin-label">Runs players can book</span>
          <div className="admin-advert-days">
            {ADVERT_DAY_CHOICES.map((days) => (
              <label key={days}>
                <input
                  type="checkbox"
                  checked={draft.days.includes(days)}
                  onChange={(event) =>
                    set({
                      days: event.target.checked
                        ? [...draft.days, days].sort((a, b) => a - b)
                        : draft.days.filter((other) => other !== days),
                    })
                  }
                />{' '}
                {days} {days === 1 ? 'day' : 'days'}
              </label>
            ))}
          </div>
        </div>
        <div className="admin-advert-prices">
          <label>
            <span>Seconds each ad shows</span>
            <Input
              type="number"
              min={5}
              max={60}
              value={draft.rotateSeconds}
              onChange={(event) => set({ rotateSeconds: Number(event.target.value) })}
            />
          </label>
          <label>
            <span>Ads per board</span>
            <Input
              type="number"
              min={1}
              max={50}
              value={draft.perBoard}
              onChange={(event) => set({ perBoard: Number(event.target.value) })}
            />
          </label>
        </div>
        <label className="admin-advert-open">
          <input type="checkbox" checked={draft.open} onChange={(event) => set({ open: event.target.checked })} />{' '}
          Players can book boards
        </label>
      </fieldset>
      {current.error && <ErrorNotice error={current.error} />}
      {editable && (
        <div className="admin-page-actions">
          <Button type="submit" disabled={!draft.days.length}>
            Save
          </Button>
          {saved && (
            <span role="status" className="admin-muted">
              Saved.
            </span>
          )}
        </div>
      )}
    </form>
  );
}
